import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentTenantFeatures } from '../hooks/useFeatures';
import { entitledProducts } from '../components/featureLayout/products';
import ProductSummaryCard from '../components/featureHome/ProductSummaryCard';
import { formatHomeDate, getFirstName, getGreeting } from '../components/featureHome/homeSummary';
import { useHomeSummary } from '../components/featureHome/useHomeSummary';

/** Static class names so Tailwind keeps them: one column per product at lg and up. */
const GRID_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-1 max-w-[560px]',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
};

/**
 * Home: a greeting and one summary card per product the tenant is entitled to
 * (the same list the sidebar Product_Switcher shows). Each card has the counts
 * that need attention, the most recent rows, and a link to the product.
 */
const LandingPage = () => {
  const { currentUser, isPlatformAdmin } = useAuth();
  const { features, loading: featuresLoading } = useCurrentTenantFeatures();

  // Platform Admins work across tenants, so they see every product (as in the sidebar).
  const products = useMemo(
    () => entitledProducts(features, isPlatformAdmin),
    [features, isPlatformAdmin]
  );
  const { cards, reload } = useHomeSummary(products);

  const now = new Date();
  const firstName = getFirstName(currentUser?.name, currentUser?.email);
  const greeting = firstName ? `${getGreeting(now)}, ${firstName}` : getGreeting(now);
  const showSkeleton = featuresLoading && !isPlatformAdmin;

  return (
    <div className="mx-auto min-w-0 max-w-[1200px]">
      <header className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[.1em] text-gray-500">{formatHomeDate(now)}</p>
        <h1 className="text-[28px] font-bold leading-tight text-gray-900">{greeting}</h1>
      </header>

      {showSkeleton ? (
        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-3"
          data-testid="home-loading"
          aria-busy="true"
        >
          {[0, 1].map((i) => (
            <div key={i} className="h-[420px] animate-pulse rounded-xl border border-gray-200 bg-white" />
          ))}
        </div>
      ) : products.length > 0 ? (
        <div className={`grid grid-cols-1 gap-4 ${GRID_COLUMNS[Math.min(products.length, 3)]}`}>
          {products.map((product) => (
            <ProductSummaryCard
              key={product.id}
              product={product}
              state={cards[product.id] ?? { status: 'loading', summary: null }}
              onRetry={reload}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-12 text-center">
          <p className="text-gray-600">
            No products are enabled for your account. Ask your administrator to enable one.
          </p>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
